import React from 'react';
import { usePetProfileUpdateContext } from '../context';
import { ACTIVITY_INFO, BODY_INFO, SNACK_LEVEL_INFO, PET_DEFAULTS } from '../types';
import { PetActivity, PetBody, SnackLevel } from '../../../core/types';
import { useSelector } from 'react-redux';
import { getPetSelector } from '../../../core/selectors';
import { KTSVG, toAbsoluteUrl, usePossessiveName } from '@helpers/index';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ScaleIcon,
  LightningIcon,
  HeartIcon,
  ArrowRightIcon,
  MinusIcon,
  PlusIcon,
  LoadingSpinner,
} from '../icons';
import { NoneIllustration, SomeIllustration, LotsIllustration } from '../icons/SnackIllustrations';

// Images for activity levels
const ActivityImages: Record<PetActivity, string> = {
  calm: '/media/img/modules/configurator/activity/calm.png',
  normal: '/media/img/modules/configurator/activity/normal.png',
  active: '/media/img/modules/configurator/activity/active.png',
};

// Images for body types
const BodyImages: Record<PetBody, string> = {
  skinny: '/media/img/modules/configurator/body/skinny.png',
  ideal: '/media/img/modules/configurator/body/ideal.png',
  chunky: '/media/img/modules/configurator/body/chunky.png',
};

export const PetInfoStep: React.FC = () => {
  const intl = useIntl();
  const { petInfo, setPetInfo, currentPetInfo, goNext, isRecalculating } =
    usePetProfileUpdateContext();
  const pet = useSelector(getPetSelector);
  const [weightInput, setWeightInput] = React.useState<string>(petInfo.weight.toString());
  const [adultWeightInput, setAdultWeightInput] = React.useState<string>(
    petInfo.adultWeight?.toString() || '',
  );
  const isPuppy = pet?.dog_stage === 'puppy';

  React.useEffect(() => {
    setWeightInput(petInfo.weight.toString());
  }, [petInfo.weight]);

  React.useEffect(() => {
    setAdultWeightInput(petInfo.adultWeight?.toString() || '');
  }, [petInfo.adultWeight]);

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(',', '.');

    // Allow empty input or just a decimal point while typing
    if (inputValue === '' || inputValue === '.') {
      setWeightInput(inputValue);
      return;
    }

    // Validate it's a number (including partial decimals like "12.")
    if (/^\d*\.?\d*$/.test(inputValue)) {
      setWeightInput(inputValue);

      const value = parseFloat(inputValue);
      // Only update global state if it's a valid complete number within range
      if (!isNaN(value) && value >= PET_DEFAULTS.WEIGHT_MIN && value <= PET_DEFAULTS.WEIGHT_MAX) {
        // Don't update if it ends with a dot to avoid parseFloat stripping it
        if (!inputValue.endsWith('.')) {
          setPetInfo({ ...petInfo, weight: value });
        }
      }
    }
  };

  const handleWeightBlur = () => {
    const value = parseFloat(weightInput);
    if (isNaN(value) || value < PET_DEFAULTS.WEIGHT_MIN || value > PET_DEFAULTS.WEIGHT_MAX) {
      setWeightInput(petInfo.weight.toString());
    } else {
      setPetInfo({ ...petInfo, weight: value });
    }
  };

  const handleAdultWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(',', '.');

    if (inputValue === '' || inputValue === '.') {
      setAdultWeightInput(inputValue);
      return;
    }

    if (/^\d*\.?\d*$/.test(inputValue)) {
      setAdultWeightInput(inputValue);

      const value = parseFloat(inputValue);
      if (!isNaN(value) && value >= PET_DEFAULTS.WEIGHT_MIN && value <= PET_DEFAULTS.WEIGHT_MAX) {
        if (!inputValue.endsWith('.')) {
          setPetInfo({ ...petInfo, adultWeight: value });
        }
      }
    }
  };

  const handleAdultWeightBlur = () => {
    const value = parseFloat(adultWeightInput);
    if (isNaN(value) || value < PET_DEFAULTS.WEIGHT_MIN || value > PET_DEFAULTS.WEIGHT_MAX) {
      setAdultWeightInput(petInfo.adultWeight?.toString() || '');
    } else {
      setPetInfo({ ...petInfo, adultWeight: value });
    }
  };

  const handleActivityChange = (activity: PetActivity) => {
    setPetInfo({ ...petInfo, activity });
  };

  const handleBodyChange = (body: PetBody) => {
    setPetInfo({ ...petInfo, body });
  };

  const handleSnackLevelChange = (snackLevel: SnackLevel) => {
    setPetInfo({ ...petInfo, snackLevel });
  };

  const hasChanges =
    petInfo.weight !== currentPetInfo.weight ||
    petInfo.activity !== currentPetInfo.activity ||
    petInfo.body !== currentPetInfo.body ||
    petInfo.snackLevel !== currentPetInfo.snackLevel ||
    petInfo.adultWeight !== currentPetInfo.adultWeight;

  const adultWeightInvalid =
    isPuppy && petInfo.adultWeight != null && petInfo.adultWeight < petInfo.weight;

  // Raw-input validity: the inputs let users type out-of-range values (so the
  // warning note can show), so the CTA must be gated on the typed value, not
  // just the committed petInfo state.
  const parsedWeight = parseFloat(weightInput);
  const weightInputInvalid =
    weightInput.trim() === '' ||
    isNaN(parsedWeight) ||
    parsedWeight < PET_DEFAULTS.WEIGHT_MIN ||
    parsedWeight > PET_DEFAULTS.WEIGHT_MAX;

  const parsedAdultWeight = parseFloat(adultWeightInput);
  const adultWeightInputInvalid =
    isPuppy &&
    adultWeightInput.trim() !== '' &&
    (isNaN(parsedAdultWeight) ||
      parsedAdultWeight < PET_DEFAULTS.WEIGHT_MIN ||
      parsedAdultWeight > PET_DEFAULTS.WEIGHT_MAX);

  const cannotProceed =
    !hasChanges ||
    isRecalculating ||
    adultWeightInvalid ||
    weightInputInvalid ||
    adultWeightInputInvalid;

  return (
    <div className="pet-info-step">
      <div className="pet-info-step__header">
        <h3>
          <FormattedMessage
            id="PET_PROFILE.INFO_STEP.TITLE"
            values={{ name: usePossessiveName(pet?.name) }}
          />
        </h3>
        <p>
          <FormattedMessage id="PET_PROFILE.INFO_STEP.SUBTITLE" />
        </p>
      </div>

      <div className="pet-info-step__section">
        <label className="pet-info-step__label">
          <ScaleIcon />
          <FormattedMessage id="PET.WEIGHT" />
        </label>
        <div className="ppu-weight-control">
          <div className="ppu-weight-control__inner">
            <button
              type="button"
              onClick={() =>
                petInfo.weight > PET_DEFAULTS.WEIGHT_MIN &&
                setPetInfo({
                  ...petInfo,
                  weight: Math.max(
                    PET_DEFAULTS.WEIGHT_MIN,
                    petInfo.weight - PET_DEFAULTS.WEIGHT_STEP,
                  ),
                })
              }
              className="ppu-weight-control__btn"
              disabled={petInfo.weight <= PET_DEFAULTS.WEIGHT_MIN}
              aria-label={intl.formatMessage({ id: 'PET.WEIGHT.DECREASE' })}>
              <MinusIcon />
            </button>
            <div className="ppu-weight-control__input-wrapper">
              <input
                type="text"
                inputMode="decimal"
                value={weightInput}
                onChange={handleWeightChange}
                onBlur={handleWeightBlur}
                className="ppu-weight-control__input"
                aria-label={intl.formatMessage({ id: 'PET.WEIGHT.ARIA_KG' })}
              />
              <span className="ppu-weight-control__unit">kg</span>
            </div>
            <button
              type="button"
              onClick={() =>
                petInfo.weight < PET_DEFAULTS.WEIGHT_MAX &&
                setPetInfo({
                  ...petInfo,
                  weight: Math.min(
                    PET_DEFAULTS.WEIGHT_MAX,
                    petInfo.weight + PET_DEFAULTS.WEIGHT_STEP,
                  ),
                })
              }
              className="ppu-weight-control__btn"
              disabled={petInfo.weight >= PET_DEFAULTS.WEIGHT_MAX}
              aria-label={intl.formatMessage({ id: 'PET.WEIGHT.INCREASE' })}>
              <PlusIcon />
            </button>
          </div>
          {petInfo.weight !== currentPetInfo.weight && (
            <p className="ppu-weight-control__change">
              {intl.formatMessage({ id: 'PET.WEIGHT.PREVIOUS' }, { weight: currentPetInfo.weight })}
            </p>
          )}
          {(() => {
            const v = parseFloat(weightInput);
            if (isNaN(v)) return null;
            if (v <= PET_DEFAULTS.WEIGHT_MIN) {
              return (
                <p className="ppu-weight-control__note text-center mt-4 text-primary">
                  {intl.formatMessage(
                    { id: 'PET.WEIGHT.MIN_NOTE' },
                    { weight: PET_DEFAULTS.WEIGHT_MIN },
                  )}
                </p>
              );
            }
            if (v >= PET_DEFAULTS.WEIGHT_MAX) {
              return (
                <p className="ppu-weight-control__note text-center mt-4 text-primary">
                  {intl.formatMessage(
                    { id: 'PET.WEIGHT.MAX_NOTE' },
                    { weight: PET_DEFAULTS.WEIGHT_MAX },
                  )}
                </p>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {isPuppy && (
        <div className="pet-info-step__section">
          <label className="pet-info-step__label">
            <ScaleIcon />
            <FormattedMessage id="PET.ADULT_WEIGHT" />
          </label>
          <div className="ppu-weight-control">
            <div className="ppu-weight-control__inner">
              <button
                type="button"
                onClick={() =>
                  (petInfo.adultWeight || 0) > PET_DEFAULTS.WEIGHT_MIN &&
                  setPetInfo({
                    ...petInfo,
                    adultWeight: Math.max(
                      PET_DEFAULTS.WEIGHT_MIN,
                      (petInfo.adultWeight || 0) - PET_DEFAULTS.WEIGHT_STEP,
                    ),
                  })
                }
                className="ppu-weight-control__btn"
                disabled={!petInfo.adultWeight || petInfo.adultWeight <= PET_DEFAULTS.WEIGHT_MIN}
                aria-label={intl.formatMessage({ id: 'PET.ADULT_WEIGHT.DECREASE' })}>
                <MinusIcon />
              </button>
              <div className="ppu-weight-control__input-wrapper">
                <input
                  type="text"
                  inputMode="decimal"
                  value={adultWeightInput}
                  onChange={handleAdultWeightChange}
                  onBlur={handleAdultWeightBlur}
                  className="ppu-weight-control__input"
                  aria-label={intl.formatMessage({ id: 'PET.ADULT_WEIGHT.ARIA_KG' })}
                />
                <span className="ppu-weight-control__unit">kg</span>
              </div>
              <button
                type="button"
                onClick={() =>
                  (petInfo.adultWeight || 0) < PET_DEFAULTS.WEIGHT_MAX &&
                  setPetInfo({
                    ...petInfo,
                    adultWeight: Math.min(
                      PET_DEFAULTS.WEIGHT_MAX,
                      (petInfo.adultWeight || 0) + PET_DEFAULTS.WEIGHT_STEP,
                    ),
                  })
                }
                className="ppu-weight-control__btn"
                disabled={
                  petInfo.adultWeight !== undefined &&
                  petInfo.adultWeight >= PET_DEFAULTS.WEIGHT_MAX
                }
                aria-label={intl.formatMessage({ id: 'PET.ADULT_WEIGHT.INCREASE' })}>
                <PlusIcon />
              </button>
            </div>
            {petInfo.adultWeight !== currentPetInfo.adultWeight && currentPetInfo.adultWeight && (
              <p className="ppu-weight-control__change">
                {intl.formatMessage(
                  { id: 'PET.WEIGHT.PREVIOUS' },
                  { weight: currentPetInfo.adultWeight },
                )}
              </p>
            )}
            {(() => {
              const v = parseFloat(adultWeightInput);
              if (isNaN(v)) return null;
              if (v < PET_DEFAULTS.WEIGHT_MIN) {
                return (
                  <p className="ppu-weight-control__note text-center mt-4 text-primary">
                    {intl.formatMessage(
                      { id: 'PET.WEIGHT.MIN_NOTE' },
                      { weight: PET_DEFAULTS.WEIGHT_MIN },
                    )}
                  </p>
                );
              }
              if (v > PET_DEFAULTS.WEIGHT_MAX) {
                return (
                  <p className="ppu-weight-control__note text-center mt-4 text-primary">
                    {intl.formatMessage(
                      { id: 'PET.WEIGHT.MAX_NOTE' },
                      { weight: PET_DEFAULTS.WEIGHT_MAX },
                    )}
                  </p>
                );
              }
              return null;
            })()}
          </div>
          {isPuppy && petInfo.adultWeight != null && petInfo.adultWeight < petInfo.weight && (
            <div
              className="calorie-gauge__pet-msg-content md:max-w-[40rem]"
              style={{ marginTop: '1rem' }}>
              <KTSVG path={toAbsoluteUrl('/media/svg/general/orange-info-icon.svg')} />
              <p>
                <FormattedMessage id="PET.WEIGHT.BELOW_ADULT_WARNING" />
              </p>
            </div>
          )}
        </div>
      )}

      {!isPuppy && (
        <div className="pet-info-step__section">
          <label className="pet-info-step__label">
            <LightningIcon />
            <FormattedMessage id="PET.ACTIVITY_LEVEL" />
          </label>
          <div className="ppu-selection-grid">
            {(Object.keys(ACTIVITY_INFO) as PetActivity[]).map((activity) => {
              const isSelected = petInfo.activity === activity;
              return (
                <button
                  key={activity}
                  type="button"
                  onClick={() => handleActivityChange(activity)}
                  className={`ppu-selection-card ${
                    isSelected ? 'ppu-selection-card--selected' : ''
                  }`}
                  aria-pressed={isSelected}>
                  <div className="ppu-selection-card__image">
                    <img
                      src={ActivityImages[activity]}
                      alt={intl.formatMessage({ id: ACTIVITY_INFO[activity].labelId })}
                    />
                  </div>
                  <div className="ppu-selection-card__title">
                    {intl.formatMessage({ id: ACTIVITY_INFO[activity].labelId })}
                  </div>
                  <div className="ppu-selection-card__description">
                    {intl.formatMessage({ id: ACTIVITY_INFO[activity].descriptionId })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!isPuppy && (
        <div className="pet-info-step__section">
          <label className="pet-info-step__label">
            <HeartIcon />
            <FormattedMessage id="PET.BODY_SHAPE" />
          </label>
          <div className="ppu-selection-grid">
            {(Object.keys(BODY_INFO) as PetBody[]).map((body) => {
              const isSelected = petInfo.body === body;
              return (
                <button
                  key={body}
                  type="button"
                  onClick={() => handleBodyChange(body)}
                  className={`ppu-selection-card ${
                    isSelected ? 'ppu-selection-card--selected' : ''
                  }`}
                  aria-pressed={isSelected}>
                  <div className="ppu-selection-card__image">
                    <img
                      src={BodyImages[body]}
                      alt={intl.formatMessage({ id: BODY_INFO[body].labelId })}
                    />
                  </div>
                  <div className="ppu-selection-card__title">
                    {intl.formatMessage({ id: BODY_INFO[body].labelId })}
                  </div>
                  <div className="ppu-selection-card__description">
                    {intl.formatMessage({ id: BODY_INFO[body].descriptionId })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="pet-info-step__section">
        <label className="pet-info-step__label">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <circle cx="9" cy="10" r="1" fill="currentColor" />
            <circle cx="15" cy="10" r="1" fill="currentColor" />
            <circle cx="12" cy="14" r="1" fill="currentColor" />
          </svg>
          <FormattedMessage id="PET.SNACKS_TREATS" />
        </label>
        <div className="ppu-selection-grid">
          {(Object.keys(SNACK_LEVEL_INFO) as SnackLevel[]).map((level) => {
            const isSelected = petInfo.snackLevel === level;
            const illustrationColor = isSelected ? '#00756c' : '#8fb5a3';
            return (
              <button
                key={level}
                type="button"
                onClick={() => handleSnackLevelChange(level)}
                className={`ppu-selection-card ${isSelected ? 'ppu-selection-card--selected' : ''}`}
                aria-pressed={isSelected}>
                <div
                  className="ppu-selection-card__image"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: '100px',
                    margin: '0 auto',
                  }}>
                  {level === 'none' && (
                    <NoneIllustration color={illustrationColor} active={isSelected} />
                  )}
                  {level === 'some' && <SomeIllustration color={illustrationColor} />}
                  {level === 'lots' && <LotsIllustration color={illustrationColor} />}
                </div>
                <div className="ppu-selection-card__title">
                  {intl.formatMessage({ id: SNACK_LEVEL_INFO[level].labelId })}
                </div>
                <div className="ppu-selection-card__description">
                  {intl.formatMessage({ id: SNACK_LEVEL_INFO[level].descriptionId })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="pet-info-step__actions">
        <button
          type="button"
          onClick={goNext}
          disabled={cannotProceed}
          className={`ppu-action-button ppu-action-button--primary ${
            isRecalculating ? 'ppu-action-button--loading' : ''
          }`}>
          {isRecalculating ? (
            <>
              <LoadingSpinner />
              <FormattedMessage id="GLOBAL.CALCULATING" />
            </>
          ) : (
            <>
              <FormattedMessage id="GLOBAL.NEXT" />
              <ArrowRightIcon />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
